import { Injectable } from "@angular/core";
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
  HttpResponse,
} from "@angular/common/http";
import { Observable, catchError, from, map, switchMap, throwError } from "rxjs";
import { environment } from "../../environments/environment";

export interface ExportedFile {
  blob: Blob;
  /** Nome del file da Content-Disposition, null se il backend non lo indica */
  fileName: string | null;
}

@Injectable({
  providedIn: "root",
})
export class OverviewHarmonizerService {
  private apiUrl = `${environment.apiUrl}/overview-harmonizer`;

  constructor(private http: HttpClient) {}

  /**
   * Export Excel di tutti i colli che corrispondono ai filtri (nessuna paginazione).
   * Con withOriginalData il file include anche i dati di consegna originali (PTV).
   */
  exportParcelsExcel(
    filters: object,
    withOriginalData: boolean,
  ): Observable<ExportedFile> {
    const params = new HttpParams().set("withOriginalData", withOriginalData);
    return this.http
      .post(`${this.apiUrl}/parcels/export`, filters, {
        params,
        observe: "response",
        responseType: "blob",
      })
      .pipe(
        map((res: HttpResponse<Blob>) => ({
          blob: res.body as Blob,
          fileName: this.extractFileName(res),
        })),
        catchError((err) => this.blobErrorToText(err)),
      );
  }

  // Con responseType "blob" anche il corpo dell'errore è un Blob: lo converte in testo
  // così ErrorHelperService può estrarre il messaggio del backend
  private blobErrorToText(err: HttpErrorResponse): Observable<never> {
    if (!(err.error instanceof Blob)) {
      return throwError(() => err);
    }
    return from(err.error.text()).pipe(
      switchMap((text) =>
        throwError(
          () =>
            new HttpErrorResponse({
              error: text,
              headers: err.headers,
              status: err.status,
              statusText: err.statusText,
              url: err.url ?? undefined,
            }),
        ),
      ),
    );
  }

  private extractFileName(res: HttpResponse<Blob>): string | null {
    const contentDisposition = res.headers.get("Content-Disposition");
    const match = contentDisposition?.match(
      /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i,
    );
    return match ? decodeURIComponent(match[1]) : null;
  }
}
